import en from './en';
import fr from './fr';
import ru from './ru';

export const translations = {
  en,
  fr,
  ru
} as const;

export type TranslationMap = typeof translations;
export type LocaleKey = keyof TranslationMap;
export type Messages = TranslationMap[LocaleKey];

export const defaultLocale: LocaleKey = 'en';

export function resolveLocale(): LocaleKey {
  const params = new URLSearchParams(window.location.search);
  const queryLang = params.get('lang')?.trim().toLowerCase();
  if (queryLang && queryLang in translations) {
    return queryLang as LocaleKey;
  }

  const browserLang = window.navigator?.language?.split('-')[0]?.toLowerCase();
  if (browserLang && browserLang in translations) {
    return browserLang as LocaleKey;
  }

  return defaultLocale;
}

export function getMessages(locale: LocaleKey): Messages {
  const base = translations[defaultLocale];
  if (locale === defaultLocale) {
    return base;
  }

  const overrides = translations[locale];

  return mergeDeep(base, overrides);
}

function mergeDeep<T extends Record<string, any>>(base: T, overrides: Partial<T>): T {
  const result: Record<string, any> = Array.isArray(base) ? [...base] : { ...base };

  for (const key of Object.keys(overrides) as Array<keyof T>) {
    const overrideValue = overrides[key];
    const baseValue = base[key];

    if (overrideValue && typeof overrideValue === 'object' && !Array.isArray(overrideValue)) {
      result[key as string] = mergeDeep(
        baseValue && typeof baseValue === 'object' ? (baseValue as Record<string, any>) : {},
        overrideValue as Record<string, any>
      );
    } else {
      result[key as string] = overrideValue;
    }
  }

  return result as T;
}
